import { Component } from 'react';

// Kept out of any lazily-loaded chunk on purpose: it has to already exist to
// catch the case where a chunk is the thing that fails to load.

const RELOAD_FLAG = 'chunkReloaded';

const isChunkLoadError = (error) => {
  const name = error?.name || '';
  const message = error?.message || '';
  return (
    name === 'ChunkLoadError' ||
    /loading chunk \S+ failed/i.test(message) ||
    /loading css chunk \S+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
};

const PALETTE = {
  dark: { bg: '#0A0A0F', fg: '#FFFFFF', muted: 'rgba(255,255,255,0.7)', line: 'rgba(255,255,255,0.12)', panel: 'rgba(255,255,255,0.06)' },
  light: { bg: '#F8FAFC', fg: '#0F172A', muted: '#475569', line: 'rgba(15,23,42,0.12)', panel: 'rgba(15,23,42,0.04)' },
};

export default class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidMount() {
    // A clean mount means the current chunks are good, so let a future stale
    // deploy have its own retry.
    if (!this.state.error) {
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        // Storage blocked - the retry simply won't be suppressed later
      }
    }
  }

  componentDidCatch(error) {
    // Every deploy renames the hashed chunk files. A tab holding the old
    // index.html asks for a chunk that no longer exists, the dynamic import
    // rejects, React tears the tree down and the page goes white - which is why
    // a manual refresh "fixed" it. Reload once to pick up the new build; the
    // flag keeps a genuine failure from looping.
    if (!isChunkLoadError(error)) return;

    let alreadyRetried = true;
    try {
      alreadyRetried = sessionStorage.getItem(RELOAD_FLAG) === 'true';
      if (!alreadyRetried) sessionStorage.setItem(RELOAD_FLAG, 'true');
    } catch {
      // Storage blocked - show the panel rather than risk a reload loop
      return;
    }

    if (!alreadyRetried) window.location.reload();
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      // Not fatal - the reload below is what matters
    }
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    const {
      children,
      variant = 'light',
      title = 'Something went wrong',
      description = 'This is usually a new version having been deployed while this tab was open. Reloading picks it up.',
    } = this.props;

    if (!error) return children;
    const c = PALETTE[variant] || PALETTE.light;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '24px',
          background: c.bg,
          color: c.fg,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <div style={{ maxWidth: '520px', textAlign: 'center' }}>
          <h1 style={{ fontSize: '20px', margin: '0 0 12px' }}>{title}</h1>
          <p style={{ color: c.muted, margin: '0 0 20px', lineHeight: 1.6 }}>{description}</p>
          <pre
            style={{
              textAlign: 'left',
              background: c.panel,
              border: `1px solid ${c.line}`,
              borderRadius: '8px',
              padding: '12px',
              margin: '0 0 20px',
              fontSize: '12px',
              color: c.muted,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {String(error?.message || error)}
          </pre>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: '12px 24px',
              borderRadius: '10px',
              border: `1px solid ${c.line}`,
              background: variant === 'dark' ? 'rgba(0,255,255,0.12)' : '#00B8D9',
              color: variant === 'dark' ? '#FFFFFF' : '#FFFFFF',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
